import type { ReactNode } from 'react'

// ─── Formatting ──────────────────────────────────────────────

export function money(value: number, fractionDigits = 0): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

export function signedMoney(value: number, fractionDigits = 0): string {
  return `${value >= 0 ? '+' : ''}${money(value, fractionDigits)}`
}

export function pct(value: number, fractionDigits = 1): string {
  return `${(value * 100).toFixed(fractionDigits)}%`
}

export function shortDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: y === new Date().getFullYear() ? undefined : 'numeric',
  })
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

export function daysUntil(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const target = new Date(y, m - 1, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}

// ─── Building blocks ─────────────────────────────────────────

export function Card({
  title,
  subtitle,
  action,
  children,
  className = '',
}: {
  title?: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 ${className}`}>
      {(title || action) && (
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            {title && <h3 className="text-slate-900 dark:text-white font-semibold">{title}</h3>}
            {subtitle && <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

export type ChipTone = 'green' | 'amber' | 'red' | 'blue' | 'slate' | 'purple'

const chipTones: Record<ChipTone, string> = {
  green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  red: 'bg-red-500/10 text-red-600 dark:text-red-400',
  blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  slate: 'bg-slate-500/10 text-slate-500 dark:text-slate-400',
  purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
}

export function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${chipTones[tone]}`}
    >
      {children}
    </span>
  )
}

export function ProgressBar({ value, max, tone = 'blue' }: { value: number; max: number; tone?: 'blue' | 'red' | 'green' }) {
  const ratio = max > 0 ? Math.min(1, value / max) : 0
  const barColor = tone === 'red' ? 'bg-red-500' : tone === 'green' ? 'bg-emerald-500' : 'bg-blue-500'
  return (
    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${barColor}`} style={{ width: `${ratio * 100}%` }} />
    </div>
  )
}

export function StatCard({
  label,
  value,
  sub,
  subTone = 'slate',
}: {
  label: string
  value: string
  sub?: string
  subTone?: 'green' | 'red' | 'slate' | 'amber'
}) {
  const subColor = {
    green: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-red-600 dark:text-red-400',
    amber: 'text-amber-600 dark:text-amber-400',
    slate: 'text-slate-500 dark:text-slate-400',
  }[subTone]
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <p className="text-slate-500 dark:text-slate-400 text-sm">{label}</p>
      <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
      {sub && <p className={`text-xs mt-1 ${subColor}`}>{sub}</p>}
    </div>
  )
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 bg-slate-200/50 dark:bg-slate-700/50 rounded-lg" />
      ))}
    </div>
  )
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Could not reach the Finances service.'
}

/**
 * Shown when a load fails. Finances has no sample-data fallback on purpose —
 * placeholder balances that look real are worse than an outage the owner can
 * see — so every loader surfaces its failure here instead.
 */
export function LoadError({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3">
      <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
      </svg>
      <div className="min-w-0">
        <p className="text-red-600 dark:text-red-400 text-sm font-medium">Could not load live data</p>
        <p className="text-red-600/80 dark:text-red-400/80 text-xs mt-0.5 break-words">{error}</p>
        {onRetry && (
          <button className={`${secondaryBtn} mt-2`} onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    </div>
  )
}

export function EmptyState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="text-center py-8">
      <div className="text-3xl mb-2">{icon}</div>
      <p className="text-slate-500 dark:text-slate-400 text-sm">{message}</p>
    </div>
  )
}

export const inputClass =
  'w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none placeholder-slate-400 dark:placeholder-slate-500'

export const primaryBtn =
  'px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors'

export const secondaryBtn =
  'px-3 py-1.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-sm rounded-lg transition-colors'
